import React from 'react';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Link } from 'react-router-dom';
import { Table, Tag, theme } from 'antd';
import { Field } from '../../components/Field';

// NOTE: this local shape mirrors @wellverse/shared-ts's AdminReferralDto.
interface ReferralParty {
  id: string;
  displayName: string;
  referralCode?: string;
}

interface ReferralReward {
  id: string;
  beneficiaryId: string;
  beneficiaryName: string;
  side: 'REFERRER' | 'REFEREE';
  days: number;
  status: string;
  creditedAt?: string | null;
  storeGrantedAt?: string | null;
  grantError?: string | null;
  createdAt: string;
}

interface ReferralDto {
  id: string;
  code: string;
  createdAt: string;
  referrer: ReferralParty;
  referee: ReferralParty;
  referrerReward?: ReferralReward | null;
  refereeReward?: ReferralReward | null;
}

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

const statusColors: Record<string, string> = {
  CREDITED: '#3ecf8e',
  PENDING: '#f59e0b',
  REVOKED: '#ef4444',
};

export const ReferralShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow<ReferralDto>({ resource: 'referrals' });
  const item = queryResult?.data?.data;

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  const rewards = [item.referrerReward, item.refereeReward].filter(
    (r): r is ReferralReward => Boolean(r),
  );

  return (
    <Show resource="referrals">
      <div style={{ padding: '8px 0' }}>
        <Field label="ID">{item.id}</Field>
        <Field label="Code Used">{item.code}</Field>
        <Field label="Referrer">
          <Link to={`/users/show/${item.referrer.id}`}>
            {item.referrer.displayName || item.referrer.id}
          </Link>
        </Field>
        {item.referrer.referralCode && (
          <Field label="Referrer Share Code">{item.referrer.referralCode}</Field>
        )}
        <Field label="Referee">
          <Link to={`/users/show/${item.referee.id}`}>
            {item.referee.displayName || item.referee.id}
          </Link>
        </Field>
        <Field label="Created At">{new Date(item.createdAt).toLocaleString()}</Field>

        <Field label="Rewards">
          {rewards.length === 0 ? (
            <span style={{ color: token.colorTextSecondary }}>
              No reward rows yet for this referral.
            </span>
          ) : (
            <Table dataSource={rewards} rowKey="id" pagination={false} size="small">
              <Table.Column
                dataIndex="side"
                title="Side"
                render={(side: string) => (side === 'REFERRER' ? 'Referrer' : 'Referee')}
              />
              <Table.Column
                title="Beneficiary"
                render={(_: unknown, record: ReferralReward) => (
                  <Link to={`/users/show/${record.beneficiaryId}`}>
                    {record.beneficiaryName || record.beneficiaryId}
                  </Link>
                )}
              />
              <Table.Column dataIndex="days" title="Free Days" />
              <Table.Column
                dataIndex="status"
                title="Status"
                render={(status: string) => (
                  <Tag style={tagStyle(statusColors[status] || '#898989')}>{status}</Tag>
                )}
              />
              <Table.Column
                dataIndex="creditedAt"
                title="Credited At"
                render={(v?: string | null) => (v ? new Date(v).toLocaleString() : '—')}
              />
              <Table.Column
                title="Granted at store"
                render={(_: unknown, reward: ReferralReward) =>
                  reward.storeGrantedAt ? (
                    new Date(reward.storeGrantedAt).toLocaleString()
                  ) : reward.grantError ? (
                    // Credited but never delivered — Apple refuses during a free
                    // trial and caps extensions at two a year, so this is the
                    // column that shows a reward still owed.
                    <Tag style={tagStyle('#d4380d')} title={reward.grantError}>
                      Not granted
                    </Tag>
                  ) : (
                    '—'
                  )
                }
              />
              <Table.Column
                dataIndex="createdAt"
                title="Recorded At"
                render={(v: string) => new Date(v).toLocaleString()}
              />
            </Table>
          )}
        </Field>
      </div>
    </Show>
  );
};
